// src/components/shipment/FilterBar.jsx
import React, { useState, useEffect, useMemo } from "react";
import { RotateCcw, SlidersHorizontal } from "lucide-react";
import SearchInputWithClear from "@/components/ui/SearchInputWithClear";
import MoreFiltersDrawer from "./MoreFiltersDrawer";
import SortDropdown from "./SortDropdown";

const FilterBar = ({
  onFilter = () => {},
  stats = { total: 0 },
  uniqueCarriers = {},
}) => {
  // 🔥 Local States
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [polFilter, setPolFilter] = useState("");
  const [podFilter, setPodFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [sortBy, setSortBy] = useState("recent");

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Applied filters (only applied on button click)
  const [appliedFilters, setAppliedFilters] = useState({
    carrier: "all",
    pol: "",
    pod: "",
    dateFrom: "",
    dateTo: "",
  });

  // 🔥 Active filter count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (carrierFilter !== "all") count++;
    if (polFilter) count++;
    if (podFilter) count++;
    if (dateRange.from) count++;
    if (dateRange.to) count++;
    return count;
  }, [carrierFilter, polFilter, podFilter, dateRange]);

  // 🔥 Central Filter Trigger

  const triggerFilter = (filter) => {
    const payload = {
      search: searchText,
      status: statusFilter,
      sortBy,
      ...filter,
    };

    onFilter(payload);
  };

  // 🔥 Debounced Search
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerFilter(appliedFilters);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // 🔥 Immediate trigger for status / sort / applied filters
  useEffect(() => {
    triggerFilter(appliedFilters);
  }, [statusFilter, sortBy]);

  const defaultMoreFilters = {
    carrier: "all",
    pol: "",
    pod: "",
    dateFrom: "",
    dateTo: "",
  };

  // Clear advanced filters only
  const clearAllFilters = () => {
    setCarrierFilter("all");
    setPolFilter("");
    setPodFilter("");
    setDateRange({ from: "", to: "" });
    setAppliedFilters(defaultMoreFilters);
    triggerFilter(defaultMoreFilters);
  };

  const hasActiveFilters =
    searchText !== "" ||
    statusFilter !== "all" ||
    sortBy !== "recent" ||
    activeFiltersCount > 0;

  const resetAllFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setSortBy("recent");
    setCarrierFilter("all");
    setPolFilter("");
    setPodFilter("");
    setDateRange({ from: "", to: "" });
    setAppliedFilters(defaultMoreFilters);
    onFilter({
      search: "",
      status: "all",
      sortBy: "recent",
      ...defaultMoreFilters,
    });
  };

  // Close advanced filters when clicking outside the filter bar
  useEffect(() => {
    if (!showMoreFilters) return;

    const handleOutside = (event) => {
      const target = event.target;
      const bar = document.getElementById("shipment-filter-bar");
      const drawer = document.getElementById("shipment-more-filters-drawer");
      if (bar?.contains(target)) return;
      if (drawer?.contains(target)) return;
      // Portaled search suggestions render on document.body, not inside the drawer node
      if (target.closest?.("[data-searchbarpagination-dropdown]")) return;
      setShowMoreFilters(false);
    };

    document.addEventListener("mousedown", handleOutside, true);
    return () => document.removeEventListener("mousedown", handleOutside, true);
  }, [showMoreFilters]);

  return (
    <div
      id="shipment-filter-bar"
      className="rounded-xl bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4"
    >
      <div className="flex items-center gap-4">
        {/* Search */}
        <SearchInputWithClear
          className="relative flex-1 max-w-xl"
          placeholder="Search by Quote ID, Customer, POL, or POD..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClear={() => setSearchText("")}
        />

        {/* Status Buttons */}
        <div className="flex items-center gap-2">
          {["all", "active", "intransit", "delayed"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {status === "all"
                ? "All"
                : status === "active"
                  ? "Active"
                  : status === "intransit"
                    ? "In Transit"
                    : "Delayed"}
            </button>
          ))}
        </div>

        {/* More Filters */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMoreFilters(!showMoreFilters);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showMoreFilters || activeFiltersCount > 0
                ? "bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30"
                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            More Filters
            {activeFiltersCount > 0 && (
              <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <MoreFiltersDrawer
            isOpen={showMoreFilters}
            onClose={() => setShowMoreFilters(false)}
            carrierFilter={carrierFilter}
            setCarrierFilter={setCarrierFilter}
            polFilter={polFilter}
            setPolFilter={setPolFilter}
            podFilter={podFilter}
            setPodFilter={setPodFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            activeFiltersCount={activeFiltersCount}
            uniqueCarriers={uniqueCarriers}
            clearAllFilters={clearAllFilters}
            onApply={() => {
              const filter = {
                carrier: carrierFilter,
                pol: polFilter,
                pod: podFilter,
                dateFrom: dateRange.from,
                dateTo: dateRange.to,
              };
              setAppliedFilters(filter);
              triggerFilter(filter);
              setShowMoreFilters(false);
            }}
          />
        </div>

        {/* Sort */}
        <SortDropdown
          sortBy={sortBy}
          setSortBy={setSortBy}
          isOpen={showSortDropdown}
          setIsOpen={setShowSortDropdown}
        />

        {/* Reset all */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetAllFilters}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}
      </div>

      {/* Results */}
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
        Showing{" "}
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {stats?.showRecord}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {stats.total}
        </span>{" "}
        shipments
      </p>
    </div>
  );
};

export default FilterBar;
